// 履歴行の「登記取得日」（法務局から PDF を受領した日・登記の鮮度）を導出するロジック。
// 1 依頼に複数種別が含まれ登記取得日が異なるケースがあるため、一覧の 1 列に畳む際は
// 全部事項優先のフォールバック（全部事項 → 所有者事項 → 電子公図 →
// 地積測量図 → 地役権図面 → 建物図面・テーブル列順）で代表値を選ぶ。
// 複数種別の中で最も新しい登記取得日を選ぶのではなく、画面の主目的である全部事項の鮮度を最優先する点に注意。
import { format, isValid, parseISO } from "date-fns";
import type { PropertyCodePictureRequestRow } from "@/features/propertyCodeAcquisition/types";
import { PICTURE_CELL_FIELD_LABELS } from "@/features/propertyCodeAcquisition/utils/pictureCellFields";

/**
 * ISO 日付文字列を安全に整形する（依頼日列・登記取得日セルで共用）。
 * null/undefined・空文字・パース不能な不正値は throw させず fallback を返す。
 * @param value ISO 日付文字列（"YYYY-MM-DD" / ISO8601）
 * @param pattern date-fns のフォーマットパターン（例: "yyyy/MM/dd"）
 * @param fallback 不正値時に返す文字列（例: 登記取得日セルは "—"、依頼日列は ""）
 * @example
 * safeFormatIsoDate("2026-02-17", "yyyy/MM/dd", "—") // "2026/02/17"
 */
export const safeFormatIsoDate = (
  value: string | null | undefined,
  pattern: string,
  fallback: string
): string => {
  if (value == null || value === "") return fallback;
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, pattern) : fallback;
};

/** 種別別の登記取得日明細 1 件（Tooltip 表示用）。 */
export interface PictureReceivedDateDetail {
  /** 種別表示名（例: 「全部事項」）。 */
  label: string;
  /** 登記取得日（"YYYY-MM-DD"・API 生値）。表示整形は呼び出し側で行う。 */
  date: string;
}

/**
 * 登記取得日を持つ種別の明細をフォールバック優先順で列挙する。
 * 未受領（依頼中/失敗）・未存在の種別は含まない。全セル null のとき []。
 */
export const listPictureReceivedDateDetails = (
  row: PropertyCodePictureRequestRow
): PictureReceivedDateDetail[] =>
  PICTURE_CELL_FIELD_LABELS.flatMap(([field, label]) => {
    // 型上 row[field] は必須だが、BE 契約違反でセルが部分欠落しても課金済み画面が全 throw しないよう
    // オプショナルチェーンで防御する（既存の null/空文字フォールバックと整合）。
    const date = row[field]?.pictureReceivedDate;
    // null・空文字に加えパース不能な日付も除外する（フォールバック値・表示・ソートを汚染させない）。
    return date != null && date !== "" && isValid(parseISO(date))
      ? [{ label, date }]
      : [];
  });

/**
 * 代表登記取得日（フォールバック先頭の日付・"YYYY-MM-DD"）。登記取得日が 1 件もなければ null。
 * 「明細の先頭」と定義することでフォールバック規則の実装を一元化する。
 */
export const getPrimaryPictureReceivedDate = (
  row: PropertyCodePictureRequestRow
): string | null => listPictureReceivedDateDetails(row)[0]?.date ?? null;
